import React, { useMemo } from 'react';
import { Responsive as ResponsiveGridLayoutOriginal } from 'react-grid-layout';
import type { Layout, LayoutItem } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { withResponsiveWidth } from '../components/DashboardEngine/components/WidthProviderHOC';
import type { UserWidgetPreferenceBase, WidgetDefinitionBase } from '../components/DashboardEngine/types';
import { ROW_HEIGHT, migrateLayout } from './utils/layoutMigration';

// Envolvemos el grid responsivo con nuestro HOC a prueba de memory leaks (Vite 8 fix)
const ResponsiveGrid = withResponsiveWidth(ResponsiveGridLayoutOriginal);

interface DashboardGridProps<
  TPreference extends UserWidgetPreferenceBase = UserWidgetPreferenceBase,
  TDefinition extends WidgetDefinitionBase = WidgetDefinitionBase
> {
  layout: TPreference[];
  catalog: TDefinition[];
  isEditMode: boolean;
  globalFilters: Record<string, unknown>;
  onLayoutChange: (newLayout: TPreference[]) => void;
  onRemoveWidget: (id: string) => void;
  renderWidget: (
    preference: TPreference,
    definition: TDefinition,
    isEditMode: boolean,
    globalFilters: Record<string, unknown>,
    onRemove: (id: string) => void
  ) => React.ReactNode;
}

export const DashboardGrid = <
  TPreference extends UserWidgetPreferenceBase,
  TDefinition extends WidgetDefinitionBase
>({
  layout,
  catalog,
  isEditMode,
  globalFilters,
  onLayoutChange,
  onRemoveWidget,
  renderWidget
}: DashboardGridProps<TPreference, TDefinition>) => {
  /**
   * Layout ya convertido al formato actual. Ver `layoutMigration.ts`: las filas sin
   * sello se convierten en memoria y el sello viaja en el siguiente guardado, así
   * que ningún cliente necesita un script ni un despliegue coordinado.
   */
  const migrated = useMemo(() => migrateLayout(layout), [layout]);

  const gridLayouts = useMemo(() => {
    const validLayouts: LayoutItem[] = migrated.map(pref => {
      const def = catalog.find(c => c.widget_id === pref.widget_id);
      return {
        i: pref.id,
        x: pref.pos_x,
        y: pref.pos_y,
        w: pref.col_span,
        h: pref.row_span,
        minW: def?.col_min || 2,
        maxW: def?.col_max || 12,
        isDraggable: isEditMode,
        isResizable: isEditMode,
      };
    });

    return { lg: validLayouts, md: validLayouts };
  }, [migrated, catalog, isEditMode]);

  const handleLayoutChange = (currentLayout: Layout) => {
    if (!isEditMode) return;

    const updatedPreferences = migrated.map(pref => {
      const gridItem = currentLayout.find(item => item.i === pref.id);
      if (gridItem) {
        return {
          ...pref,
          pos_x: gridItem.x,
          pos_y: gridItem.y,
          col_span: gridItem.w,
          row_span: gridItem.h,
          modificado_en: new Date().toISOString()
        } as TPreference;
      }
      return pref;
    });

    onLayoutChange(updatedPreferences);
  };

  return (
    <div className={`min-h-[500px] transition-all duration-300 ${isEditMode ? 'bg-gray-50/50 rounded-xl outline outline-2 outline-dashed outline-gray-200' : ''}`}>
      <ResponsiveGrid
        className="layout"
        layouts={gridLayouts}
        measureBeforeMount={true}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 8, sm: 4, xs: 2, xxs: 1 }}
        rowHeight={ROW_HEIGHT}
        margin={[12, 12]}
        preventCollision={false}
        onLayoutChange={handleLayoutChange}
        isDraggable={isEditMode}
        isResizable={isEditMode}
        draggableHandle=".drag-handle"
        compactType="vertical"
        useCSSTransforms={true}
      >
        {migrated.map((pref, index) => {
          const definition = catalog.find(c => c.widget_id === pref.widget_id);
          /**
           * Sin definición en el catálogo, el widget NO se pinta.
           *
           * Antes se dejaba un "Widget desconocido" ocupando su celda. Eso pasa
           * cuando alguien desactiva un widget del catálogo o el usuario pierde el
           * permiso que lo concedía —los dos casos normales—, y en ninguno tiene
           * sentido dejar una lápida en medio del tablero.
           */
          if (!definition) return null;

          return (
            /**
             * Entrada escalonada, sólo al montar. El retardo se corta a los 300 ms:
             * con veinte widgets, un stagger sin techo dejaría el último entrando
             * más de medio segundo después — se leería como lentitud, no como ritmo.
             */
            <div
              key={pref.id}
              className="dash-card-enter"
              style={{ '--dash-enter-delay': `${Math.min(index * 30, 300)}ms` } as React.CSSProperties}
            >
              {renderWidget(pref, definition, isEditMode, globalFilters, onRemoveWidget)}
            </div>
          );
        })}
      </ResponsiveGrid>
    </div>
  );
};
